const configuredBaseUrl = process.env.REACT_APP_COMPLIANCE_API_URL?.trim();
const runtimeBaseUrl = window.__APP_CONFIG__?.COMPLIANCE_API_URL?.trim();

export const complianceApiBaseUrl = (runtimeBaseUrl || configuredBaseUrl || 'https://xdms-comp-recorder-api.x-dream-media.com').replace(/\/$/, '');

export type ComplianceJobStatus = 'created' | 'starting' | 'running' | 'stopping' | 'stopped' | 'completed' | 'failed' | 'interrupted';
export interface ComplianceChoice { id: string; name: string }
export interface ComplianceSettings {
  sources: ComplianceChoice[];
  presets: Array<ComplianceChoice & { fileName: string }>;
  storageLocations: ComplianceChoice[];
  defaultSegmentDurationMs: number;
  hlsPlaylistWindowMs: number;
}
export interface ComplianceJob {
  id: string; name: string; sourceId: string; sourceName: string; presetId: string; presetName: string;
  storageId: string; storageName: string; outputPrefix: string; outputPattern: string; segmentDurationMs: number;
  status: ComplianceJobStatus; pid: number | null; exitCode: number | null; error: string | null;
  createdAt: string; startedAt: string | null; stoppedAt: string | null; updatedAt: string;
}
export interface ComplianceJobInput {
  name: string; sourceId: string; presetId: string; storageId: string; outputPrefix?: string;
  segmentDurationMs: number; autoStart?: boolean;
}
export interface ComplianceLogEntry {
  sequence: number; timestamp: string; stream: 'system' | 'stdout' | 'stderr'; message: string;
}
export interface ComplianceRecording {
  id: string; jobId: string; channel: string; fileName: string; sizeBytes: number;
  createdAt: string; modifiedAt: string; streamUrl: string;
}
interface ItemResponse<T> { items: T[]; total: number }

const request = async <T>(path: string, options?: RequestInit): Promise<T> => {
  const headers = options?.body ? { 'Content-Type': 'application/json', ...options.headers } : options?.headers;
  const response = await fetch(`${complianceApiBaseUrl}${path}`, {
    ...options,
    headers,
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (body?.error?.message) message = body.error.message;
    } catch { /* The response did not contain JSON. */ }
    throw new Error(message);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
};

export const complianceApi = {
  getSettings: () => request<ComplianceSettings>('/api/settings'),
  getJobs: () => request<ItemResponse<ComplianceJob>>('/api/jobs'),
  createJob: (input: ComplianceJobInput) => request<ComplianceJob>('/api/jobs', { method: 'POST', body: JSON.stringify(input) }),
  updateJob: (id: string, input: Omit<ComplianceJobInput, 'autoStart'>) =>
    request<ComplianceJob>(`/api/jobs/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteJob: (id: string) => request<void>(`/api/jobs/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  startJob: (id: string) => request<ComplianceJob>(`/api/jobs/${encodeURIComponent(id)}/start`, { method: 'POST' }),
  stopJob: (id: string) => request<ComplianceJob>(`/api/jobs/${encodeURIComponent(id)}/stop`, { method: 'POST' }),
  getLogs: (id: string) => request<{ items: ComplianceLogEntry[]; lastSequence: number }>(
    `/api/jobs/${encodeURIComponent(id)}/logs?after=0&limit=1000`,
  ),
  getRecordings: async (from: string, to: string) => {
    const query = new URLSearchParams({ from, to });
    const result = await request<ItemResponse<ComplianceRecording>>(`/api/recordings?${query}`);
    return {
      ...result,
      items: result.items.map((item) => ({
        ...item,
        streamUrl: new URL(item.streamUrl, `${complianceApiBaseUrl}/`).toString(),
      })),
    };
  },
  getHlsPlaylistUrl: (sourceId: string, from: string, to: string) => {
    const query = new URLSearchParams({ sourceId, from, to });
    return `${complianceApiBaseUrl}/api/hls/playlist.m3u8?${query}`;
  },
  isHlsPlaylistReady: async (url: string) => {
    const response = await fetch(url, { cache: 'no-store' });
    return response.ok;
  },
};
